import { CommonModule } from '@angular/common';
import { Component, Input, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import {
  NbButtonModule,
  NbCardModule,
  NbCheckboxModule,
  NbDialogRef,
  NbIconModule,
  NbInputModule,
  NbSpinnerModule,
  NbToastrService,
} from '@nebular/theme';
import { finalize } from 'rxjs/operators';
import { Mensagem, MensagemParametrosRequest } from '../../../core/api/mensagem.dto';
import { MensagemService } from '../../../core/api/mensagem.service';
import { mensagemDeErro } from '../../../utils/mensagem-erro';

/**
 * Uma mensagem: o que foi mandado, o que o serviço respondeu, e o ciclo de diagnóstico —
 * trocar o remetente e reenviar para o próprio e-mail até descobrir qual combinação o serviço
 * aceita, sem mexer em configuração nem reiniciar a aplicação.
 *
 * O corpo é HTML completo vindo de formulário público: vai para um `iframe` com `sandbox`
 * vazio (sem scripts, sem formulários, sem navegação), **nunca** injetado no DOM da página.
 */
@Component({
  selector: 'app-mensagem-detalhe-dialog',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    NbCardModule,
    NbButtonModule,
    NbCheckboxModule,
    NbIconModule,
    NbInputModule,
    NbSpinnerModule,
  ],
  templateUrl: './mensagem-detalhe.dialog.html',
  styleUrl: './mensagem-detalhe.dialog.scss',
})
export class MensagemDetalheDialogComponent implements OnInit {
  @Input() mensagemId = '';

  mensagem: Mensagem | null = null;
  carregando = false;
  salvando = false;
  reenviando = false;
  erro: string | null = null;

  /** Cópia editável dos parâmetros. Só o que mudar vai no `PUT` (atualização parcial). */
  remetente = '';
  assunto = '';
  url = '';

  /** Reenvio: para o mesmo destinatário, ou para outro informado aqui. */
  paraOutro = false;
  outroDestinatario = '';
  resultadoReenvio: string | null = null;

  /** Verdadeiro se algo foi salvo — a listagem recarrega ao fechar. */
  private mudou = false;

  constructor(
    private ref: NbDialogRef<MensagemDetalheDialogComponent>,
    private service: MensagemService,
    private toastr: NbToastrService,
    private sanitizer: DomSanitizer,
  ) {}

  ngOnInit(): void {
    this.carregar();
  }

  carregar(): void {
    if (!this.mensagemId) return;
    this.carregando = true;
    this.erro = null;
    this.service
      .buscarPorId(this.mensagemId)
      .pipe(finalize(() => (this.carregando = false)))
      .subscribe({
        next: (m) => {
          this.mensagem = m;
          this.remetente = m.remetente ?? '';
          this.assunto = m.assunto ?? '';
          this.url = m.url ?? '';
        },
        error: (err) => (this.erro = mensagemDeErro(err, 'Não foi possível carregar a mensagem.')),
      });
  }

  /** O corpo entra no `iframe` por `srcdoc`; o sandbox é quem o mantém inerte. */
  get corpoSeguro(): SafeHtml {
    return this.sanitizer.bypassSecurityTrustHtml(this.mensagem?.corpo ?? '');
  }

  get corpoBruto(): string {
    return this.mensagem?.corpo ?? '';
  }

  /** `PUT` parcial: manda só o que difere do que está gravado. */
  salvarParametros(): void {
    if (!this.mensagem?.id || this.salvando) return;

    const alteracoes: MensagemParametrosRequest = {};
    if (this.remetente !== (this.mensagem.remetente ?? '')) alteracoes.remetente = this.remetente;
    if (this.assunto !== (this.mensagem.assunto ?? '')) alteracoes.assunto = this.assunto;
    if (this.url !== (this.mensagem.url ?? '')) alteracoes.url = this.url;

    if (!Object.keys(alteracoes).length) {
      this.toastr.info('Nada mudou nos parâmetros.', 'Mensagem');
      return;
    }

    this.salvando = true;
    this.service
      .atualizarParametros(this.mensagem.id, alteracoes)
      .pipe(finalize(() => (this.salvando = false)))
      .subscribe({
        next: (m) => {
          this.mensagem = m;
          this.mudou = true;
          this.toastr.success('Parâmetros atualizados.', 'Mensagem');
        },
        error: (err) =>
          this.toastr.danger(mensagemDeErro(err, 'Não foi possível salvar os parâmetros.'), 'Erro'),
      });
  }

  reenviar(): void {
    if (!this.mensagem?.id || this.reenviando) return;

    const destino = this.paraOutro ? this.outroDestinatario.trim() : '';
    if (this.paraOutro && !destino) {
      this.toastr.warning('Informe o e-mail para onde reenviar.', 'Reenvio');
      return;
    }

    this.reenviando = true;
    this.resultadoReenvio = null;
    this.service
      .reenviar(this.mensagem.id, destino)
      .pipe(finalize(() => (this.reenviando = false)))
      .subscribe({
        next: (r) => {
          const resposta = r.respostaServico?.trim() || '(o serviço respondeu vazio)';
          this.resultadoReenvio = `Reenviada para ${r.destinatario} por ${r.remetente}. Resposta do serviço: ${resposta}`;
          this.toastr.success('Reenvio disparado.', 'Reenvio');
        },
        error: (err) =>
          this.toastr.danger(mensagemDeErro(err, 'Não foi possível reenviar.'), 'Erro'),
      });
  }

  fechar(): void {
    this.ref.close(this.mudou);
  }
}
